import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  RefreshCw, Plus, Calendar, List, LayoutGrid, ChevronUp, X, Check,
} from "lucide-react";
import { MobileFilterToggle, MobileFilterClose, sidebarClasses } from "@/components/PageWithSidebar";

const STATUS_OPTIONS = [
  { key: "draft", label: "Phiếu tạm" },
  { key: "balanced", label: "Đã cân bằng kho" },
  { key: "cancelled", label: "Đã hủy" },
];

const TIME_OPTIONS = [
  { key: "today", label: "Hôm nay" },
  { key: "this_month", label: "Tháng này" },
  { key: "30days", label: "30 ngày qua" },
  { key: "90days", label: "90 ngày qua" },
  { key: "365days", label: "1 năm qua" },
  { key: "all", label: "Tất cả thời gian" },
  { key: "custom", label: "Lựa chọn khác" },
];

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      if (dateStr.includes("T")) {
        const p = dateStr.split("T"), dp = p[0].split("-"), tp = (p[1]||"").split(":").slice(0,2).join(":");
        return `${dp[2]}/${dp[1]}/${dp[0]} ${tp}`;
      }
      return dateStr;
    }
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  } catch { return dateStr; }
}

export default function StockTakesPage() {
  const [searchCode, setSearchCode] = useState("");
  const [searchProduct, setSearchProduct] = useState("");
  const [timeFilter, setTimeFilter] = useState("30days");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilters, setStatusFilters] = useState(["draft", "balanced"]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilter, setShowFilter] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);

  const queryParams = useMemo(() => ({
    search: searchCode || undefined,
    search_product: searchProduct || undefined,
    time_filter: timeFilter,
    date_from: timeFilter === "custom" ? dateFrom : undefined,
    date_to: timeFilter === "custom" ? dateTo : undefined,
    status: statusFilters.length > 0 ? statusFilters.join(",") : undefined,
    page, page_size: pageSize,
  }), [searchCode, searchProduct, timeFilter, dateFrom, dateTo, statusFilters, page, pageSize]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["stocktakes", queryParams],
    queryFn: () => api.get("/stocktakes", { params: queryParams }).then(r => r.data),
    keepPreviousData: true,
  });

  const items = data?.stocktakes || [];
  const totalCount = data?.total || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const toggleStatus = (key) => { setStatusFilters(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]); setPage(1); };

  const handleSync = async () => {
    setSyncing(true);
    try { const res = await api.post("/stocktakes/sync"); toast.success(res.data.message); refetch(); }
    catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
    finally { setSyncing(false); }
  };

  const handleCancel = async (id) => {
    try { await api.post(`/stocktakes/${id}/cancel`); toast.success("Đã hủy phiếu"); refetch(); setCancelDialog(null); setExpandedRow(null); }
    catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
  };

  const handleBalance = async (id) => {
    try { await api.post(`/stocktakes/${id}/balance`); toast.success("Đã cân bằng kho"); refetch(); }
    catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
  };

  const handleExport = () => {
    const headers = ["Mã kiểm kho","Thời gian","Ngày cân bằng","Tổng chênh lệch","SL lệch tăng","SL lệch giảm","Ghi chú","Trạng thái"];
    const rows = items.map(i => [i.code, formatDateTime(i.created_date), formatDateTime(i.balanced_date), i.total_diff, i.increase_qty, i.decrease_qty, i.note, i.status_label]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `kiem_kho_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success("Đã xuất file CSV");
  };

  return (
    <div className="flex h-full bg-white dark:bg-slate-900" data-testid="stocktakes-page">
      {/* Left Sidebar */}
      <aside className={sidebarClasses(showFilter)}>
        <MobileFilterClose setShowFilter={setShowFilter} />
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Tìm kiếm<ChevronUp className="w-4 h-4" /></h4>
          <Input placeholder="Theo mã phiếu kiểm" value={searchCode} onChange={e=>{setSearchCode(e.target.value);setPage(1);}} className="mb-2 text-sm" data-testid="search-st-code" />
          <Input placeholder="Theo mã, tên hàng" value={searchProduct} onChange={e=>{setSearchProduct(e.target.value);setPage(1);}} className="text-sm" data-testid="search-st-product" />
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Trạng thái<ChevronUp className="w-4 h-4" /></h4>
          <div className="space-y-1.5">
            {STATUS_OPTIONS.map(opt => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={statusFilters.includes(opt.key)} onCheckedChange={() => toggleStatus(opt.key)} data-testid={`st-status-${opt.key}`} />
                <span className="text-sm text-gray-600 dark:text-slate-400">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Thời gian<ChevronUp className="w-4 h-4" /></h4>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="time" checked={timeFilter!=="custom"} onChange={()=>setTimeFilter("30days")} className="accent-blue-600" />
              <select value={timeFilter==="custom"?"30days":timeFilter} onChange={e=>{setTimeFilter(e.target.value);setPage(1);}} className="text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200 flex-1" data-testid="st-time-filter">
                {TIME_OPTIONS.filter(t=>t.key!=="custom").map(t=>(<option key={t.key} value={t.key} className="bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200">{t.label}</option>))}
              </select>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="time" checked={timeFilter==="custom"} onChange={()=>setTimeFilter("custom")} className="accent-blue-600" />
              <span className="text-sm text-gray-600 dark:text-slate-400">Lựa chọn khác</span><Calendar className="w-4 h-4 text-gray-400" />
            </label>
            {timeFilter==="custom"&&(<div className="pl-6 space-y-1"><Input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="text-xs" /><Input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="text-xs" /></div>)}
          </div>
        </div>

        <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
          <label className="text-sm text-gray-600 dark:text-slate-400 flex items-center gap-2">Số bản ghi:
            <select value={pageSize} onChange={e=>{setPageSize(Number(e.target.value));setPage(1);}} className="border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200 text-sm" data-testid="st-page-size">
              <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
            </select>
          </label>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 gap-2 border-b border-gray-200 dark:border-slate-700">
          <h1 className="text-xl font-bold text-gray-800 dark:text-slate-100" data-testid="st-title">Phiếu kiểm kho</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={()=>setCreateDialog(true)} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-create-st"><Plus className="w-4 h-4" /> Kiểm kho</Button>
            <Button onClick={handleSync} variant="outline" className="text-sm gap-1" disabled={syncing} data-testid="btn-sync-st"><RefreshCw className={`w-4 h-4 ${syncing?"animate-spin":""}`} />{syncing?"Đồng bộ...":"Đồng bộ"}</Button>
            <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-export-st"><Download className="w-4 h-4" /> Xuất file</Button>
            <div className="flex border border-gray-300 dark:border-slate-600 rounded overflow-hidden">
              <button className="p-1.5 bg-blue-600 text-white"><List className="w-4 h-4" /></button>
              <button className="p-1.5 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300"><LayoutGrid className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm" data-testid="st-table">
            <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-slate-700">
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Mã kiểm kho</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Thời gian</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Ngày cân bằng</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300">Tổng chênh lệch</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300">SL lệch tăng</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300">SL lệch giảm</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Ghi chú</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {isLoading?(<tr><td colSpan={8} className="text-center py-10 text-gray-400">Đang tải...</td></tr>):items.length===0?(<tr><td colSpan={8} className="text-center py-10 text-gray-400"><div className="flex flex-col items-center gap-2"><span>Không tìm thấy phiếu kiểm kho nào phù hợp.</span><button onClick={()=>{setTimeFilter("all");setPage(1);}} className="text-blue-600 dark:text-blue-400 hover:underline text-sm">Nhấn vào đây để tìm kiếm trên toàn thời gian.</button></div></td></tr>):(
                items.map(item=>(
                  <React.Fragment key={item.id}>
                    <tr className={`border-b border-gray-100 dark:border-slate-700 cursor-pointer transition-colors ${expandedRow===item.id?"bg-blue-50 dark:bg-blue-900/30 font-medium":"hover:bg-gray-50 dark:hover:bg-slate-800/50"}`} onClick={()=>setExpandedRow(expandedRow===item.id?null:item.id)} data-testid={`st-row-${item.code}`}>
                      <td className="px-3 py-2.5 text-gray-800 dark:text-slate-200">{item.code}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{formatDateTime(item.created_date)}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{formatDateTime(item.balanced_date)}</td>
                      <td className={`px-3 py-2.5 text-right font-medium ${item.total_diff>0?"text-green-600":item.total_diff<0?"text-red-600":"text-gray-600"}`}>{item.total_diff}</td>
                      <td className="px-3 py-2.5 text-right text-green-600">{item.increase_qty||""}</td>
                      <td className="px-3 py-2.5 text-right text-red-600">{item.decrease_qty||""}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400 max-w-[150px] truncate">{item.note}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{item.status_label}</td>
                    </tr>
                    {expandedRow===item.id&&(
                      <tr className="bg-blue-50/30 dark:bg-blue-900/10"><td colSpan={8} className="p-0">
                        <StockTakeDetail item={item} onBalance={()=>handleBalance(item.id)} onCancel={()=>setCancelDialog(item)} />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 py-2 sm:py-3 gap-2 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-1">
            <button onClick={()=>setPage(1)} disabled={page<=1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsLeft className="w-4 h-4" /></button>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page<=1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            {Array.from({length:Math.min(5,totalPages)},(_,i)=>{let pn;if(totalPages<=5)pn=i+1;else if(page<=3)pn=i+1;else if(page>=totalPages-2)pn=totalPages-4+i;else pn=page-2+i;return(<button key={pn} onClick={()=>setPage(pn)} className={`w-8 h-8 rounded text-sm ${page===pn?"bg-blue-600 text-white":"hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-400"}`}>{pn}</button>);})}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={()=>setPage(totalPages)} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsRight className="w-4 h-4" /></button>
          </div>
          <span className="text-sm text-gray-500 dark:text-slate-400" data-testid="st-pagination-info">Hiển thị {totalCount>0?(page-1)*pageSize+1:0} - {Math.min(page*pageSize,totalCount)} trên tổng số {totalCount} phiếu kiểm kho</span>
        </div>
      </main>

      <MobileFilterToggle showFilter={showFilter} setShowFilter={setShowFilter} />

      <Dialog open={!!cancelDialog} onOpenChange={()=>setCancelDialog(null)}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Xác nhận hủy phiếu kiểm kho</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600 dark:text-slate-400">Bạn có chắc muốn hủy phiếu <strong>{cancelDialog?.code}</strong>?</p>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={()=>setCancelDialog(null)}>Đóng</Button><Button variant="destructive" onClick={()=>handleCancel(cancelDialog?.id)} data-testid="confirm-cancel-st">Hủy phiếu</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateStockTakeDialog open={createDialog} onClose={()=>setCreateDialog(false)} onSuccess={()=>{setCreateDialog(false);refetch();}} />
    </div>
  );
}

function StockTakeDetail({ item, onBalance, onCancel }) {
  return (
    <div className="px-6 py-4 border-t border-blue-100 dark:border-blue-800" data-testid="st-detail-panel">
      <div className="grid grid-cols-2 gap-x-12 gap-y-3 mb-4">
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 w-32">Mã kiểm kho:</span><span className="text-sm font-medium">{item.code}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 w-32">Trạng thái:</span><span className={`text-sm font-medium ${item.status==="balanced"?"text-green-600":item.status==="cancelled"?"text-red-600":"text-yellow-600"}`}>{item.status_label}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 w-32">Thời gian:</span><span className="text-sm">{formatDateTime(item.created_date)}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 w-32">Ngày cân bằng:</span><span className="text-sm">{formatDateTime(item.balanced_date)||"-"}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 w-32">Người tạo:</span><span className="text-sm">{item.creator_name}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 w-32">Chi nhánh:</span><span className="text-sm">{item.branch_name}</span></div>
        {item.note && <div className="flex items-center gap-3 col-span-2"><span className="text-sm text-gray-500 w-32">Ghi chú:</span><span className="text-sm italic">{item.note}</span></div>}
      </div>

      {item.details && item.details.length > 0 && (
        <div className="border border-gray-200 dark:border-slate-700 rounded overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-slate-700"><tr>
              <th className="px-3 py-2 text-left font-medium">Mã hàng hóa</th>
              <th className="px-3 py-2 text-left font-medium">Tên hàng</th>
              <th className="px-3 py-2 text-right font-medium">SL hệ thống</th>
              <th className="px-3 py-2 text-right font-medium">SL thực tế</th>
              <th className="px-3 py-2 text-right font-medium">Chênh lệch</th>
            </tr></thead>
            <tbody>{item.details.map((d,idx)=>(<tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
              <td className="px-3 py-2">{d.product_code}</td>
              <td className="px-3 py-2">{d.product_name}</td>
              <td className="px-3 py-2 text-right">{d.system_qty}</td>
              <td className="px-3 py-2 text-right">{d.actual_qty}</td>
              <td className={`px-3 py-2 text-right font-medium ${d.diff>0?"text-green-600":d.diff<0?"text-red-600":""}`}>{d.diff>0?`+${d.diff}`:d.diff}</td>
            </tr>))}</tbody>
          </table>
        </div>
      )}

      <div className="flex justify-center gap-2 pt-3 border-t border-gray-200 dark:border-slate-700">
        {item.status === "draft" && (
          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs gap-1" onClick={onBalance} data-testid="btn-balance-st"><Check className="w-3 h-3" /> Cân bằng kho</Button>
        )}
        {item.status !== "cancelled" && (
          <Button size="sm" variant="destructive" className="text-xs gap-1" onClick={onCancel} data-testid="btn-cancel-st"><X className="w-3 h-3" /> Hủy bỏ</Button>
        )}
      </div>
    </div>
  );
}

function CreateStockTakeDialog({ open, onClose, onSuccess }) {
  const [note, setNote] = useState("");
  const [items, setItems] = useState([{ product_code: "", product_name: "", system_qty: 0, actual_qty: 0 }]);
  const [submitting, setSubmitting] = useState(false);

  const addItem = () => setItems([...items, { product_code: "", product_name: "", system_qty: 0, actual_qty: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_,i)=>i!==idx));
  const updateItem = (idx, field, value) => { const n=[...items]; n[idx]={...n[idx],[field]:value}; setItems(n); };

  const handleSubmit = async () => {
    if (items.every(i => !i.product_name)) { toast.error("Vui lòng thêm ít nhất 1 sản phẩm"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/stocktakes", { note, items: items.filter(i=>i.product_name) });
      toast.success(res.data.message); setNote("");
      setItems([{ product_code: "", product_name: "", system_qty: 0, actual_qty: 0 }]);
      onSuccess();
    } catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Tạo phiếu kiểm kho</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><label className="text-sm font-medium mb-1 block">Ghi chú</label><Input placeholder="Ghi chú kiểm kho..." value={note} onChange={e=>setNote(e.target.value)} data-testid="create-st-note" /></div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium">Danh sách hàng kiểm</label>
              <Button size="sm" variant="outline" onClick={addItem} className="text-xs gap-1"><Plus className="w-3 h-3" /> Thêm</Button>
            </div>
            <div className="border border-gray-200 dark:border-slate-700 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 dark:bg-slate-700"><tr><th className="px-2 py-1.5 text-left text-xs">Mã hàng</th><th className="px-2 py-1.5 text-left text-xs">Tên hàng</th><th className="px-2 py-1.5 text-right text-xs">SL hệ thống</th><th className="px-2 py-1.5 text-right text-xs">SL thực tế</th><th className="px-2 py-1.5 w-8"></th></tr></thead>
                <tbody>{items.map((item,idx)=>(<tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
                  <td className="px-2 py-1"><Input className="text-xs h-7" value={item.product_code} onChange={e=>updateItem(idx,"product_code",e.target.value)} /></td>
                  <td className="px-2 py-1"><Input className="text-xs h-7" value={item.product_name} onChange={e=>updateItem(idx,"product_name",e.target.value)} placeholder="Tên SP" /></td>
                  <td className="px-2 py-1"><Input type="number" className="text-xs h-7 text-right" value={item.system_qty} onChange={e=>updateItem(idx,"system_qty",Number(e.target.value))} /></td>
                  <td className="px-2 py-1"><Input type="number" className="text-xs h-7 text-right" value={item.actual_qty} onChange={e=>updateItem(idx,"actual_qty",Number(e.target.value))} /></td>
                  <td className="px-2 py-1">{items.length>1&&<button onClick={()=>removeItem(idx)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>}</td>
                </tr>))}</tbody>
              </table>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-4"><Button variant="outline" onClick={onClose}>Đóng</Button><Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-create-st">{submitting?"Đang tạo...":"Tạo phiếu kiểm kho"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
